import startImg from '@/assets/images/caret-right-fill.svg';
import pauseImg from '@/assets/images/pause-fill.svg';
import { pauseImageTimer, resumeImageTimer } from '@/helpers/mediaUtils';
import { pauseAllProgressBars, resumeAllProgressBars } from '@/modules/actions/headerLineDuration';

const LONG_PRESS_DELAY_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;
const CONTROL_ICON_SELECTOR = '.storeeel-video-control';
const START_ICON_ID = 'start-desktop';
const PAUSE_ICON_ID = 'pause-desktop';
const HAS_POINTER_EVENTS = typeof window !== 'undefined' && 'PointerEvent' in window;
const boundElements = new WeakSet();
const boundWrappers = new WeakSet();
const observedWrappers = new WeakSet();

const isMediaVisible = (media) => {
  if (!media || !(media instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(media);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
  const rect = media.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const findVisibleMedia = (wrapper, selector) => {
  if (!wrapper) return null;
  const candidates = Array.from(wrapper.querySelectorAll(selector));
  return candidates.find((media) => isMediaVisible(media)) || null;
};

const getVisibleMedia = (wrapper) => ({
  video: findVisibleMedia(wrapper, 'video'),
  image: findVisibleMedia(wrapper, 'img'),
});

const preventContextMenu = (event) => {
  event.preventDefault();
};

const preventDragStart = (event) => {
  event.preventDefault();
};

const clearLongPressTimer = (timerRef) => {
  if (!timerRef.value) return;
  window.clearTimeout(timerRef.value);
  timerRef.value = null;
};

const findControlIcon = () => document.querySelector(CONTROL_ICON_SELECTOR);

const setControlIconToStart = (icon) => {
  icon.src = startImg;
  icon.alt = 'start';
  icon.id = START_ICON_ID;
};

const setControlIconToPause = (icon) => {
  icon.src = pauseImg;
  icon.alt = 'pause';
  icon.id = PAUSE_ICON_ID;
};

const switchControlIconToStart = () => {
  const icon = findControlIcon();
  if (!icon) return false;
  if (icon.alt === 'start' || icon.id === START_ICON_ID) return false;
  setControlIconToStart(icon);
  return true;
};

const switchControlIconToPause = () => {
  const icon = findControlIcon();
  if (!icon) return;
  setControlIconToPause(icon);
};

const attachWrapperHandlers = (wrapper) => {
  if (!wrapper || boundWrappers.has(wrapper)) return;
  boundWrappers.add(wrapper);

  wrapper.setAttribute('draggable', 'false');
  wrapper.style.setProperty('-webkit-user-drag', 'none');
  wrapper.style.setProperty('-webkit-touch-callout', 'none');
  wrapper.style.setProperty('-webkit-user-select', 'none');
  wrapper.style.userSelect = 'none';
  wrapper.addEventListener('dragstart', preventDragStart);
  wrapper.addEventListener('contextmenu', preventContextMenu);

  const longPressTimer = { value: null };
  let longPressOrigin = null;
  let pausedByLongPress = false;
  let pausedVideo = null;
  let imageRemainingTime = 0;
  let controlIconChanged = false;
  let activePointerType = null;
  const toleranceSquared = LONG_PRESS_MOVE_TOLERANCE_PX * LONG_PRESS_MOVE_TOLERANCE_PX;

  const resumeIfPaused = () => {
    clearLongPressTimer(longPressTimer);
    longPressOrigin = null;
    activePointerType = null;
    if (!pausedByLongPress) return;
    pausedByLongPress = false;

    if (pausedVideo) {
      const playPromise = pausedVideo.play();
      resumeAllProgressBars();
      if (controlIconChanged) {
        switchControlIconToPause();
        controlIconChanged = false;
      }
      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
      pausedVideo = null;
      imageRemainingTime = 0;
      return;
    }

    if (imageRemainingTime > 0) {
      resumeImageTimer(undefined, imageRemainingTime);
      imageRemainingTime = 0;
      resumeAllProgressBars();
      if (controlIconChanged) {
        switchControlIconToPause();
        controlIconChanged = false;
      }
      return;
    }

    if (controlIconChanged) {
      switchControlIconToPause();
      controlIconChanged = false;
    }
  };

  const handleLongPress = () => {
    clearLongPressTimer(longPressTimer);

    const { video, image } = getVisibleMedia(wrapper);

    if (video && !video.paused) {
      video.pause();
      pausedVideo = video;
      pausedByLongPress = true;
      pauseAllProgressBars();
      controlIconChanged = switchControlIconToStart();
      return;
    }

    if (image) {
      const remaining = pauseImageTimer();
      if (remaining <= 0) return;
      imageRemainingTime = remaining;
      pausedByLongPress = true;
      pauseAllProgressBars();
      controlIconChanged = switchControlIconToStart();
    }
  };

  const startLongPress = (point, pointerType) => {
    clearLongPressTimer(longPressTimer);
    longPressOrigin = point;
    activePointerType = pointerType;
    controlIconChanged = false;
    longPressTimer.value = window.setTimeout(() => {
      longPressTimer.value = null;
      handleLongPress();
    }, LONG_PRESS_DELAY_MS);
  };

  const handleMovePoint = (point, pointerType) => {
    if (activePointerType !== pointerType || !longPressTimer.value || !longPressOrigin) return;
    const deltaX = point.x - longPressOrigin.x;
    const deltaY = point.y - longPressOrigin.y;
    if (deltaX * deltaX + deltaY * deltaY <= toleranceSquared) return;
    clearLongPressTimer(longPressTimer);
    longPressOrigin = null;
    activePointerType = null;
    controlIconChanged = false;
  };

  const finishInteraction = (pointerType) => {
    if (activePointerType !== pointerType) return;
    resumeIfPaused();
  };

  const handleTouchStart = (event) => {
    if (activePointerType === 'touch' && longPressTimer.value) return;
    if (event.touches?.length > 1) {
      clearLongPressTimer(longPressTimer);
      return;
    }
    const touch = event.touches?.[0];
    if (!touch) return;
    startLongPress(
      { x: touch.clientX ?? touch.pageX ?? 0, y: touch.clientY ?? touch.pageY ?? 0 },
      'touch'
    );
  };

  const handleTouchMove = (event) => {
    if (!longPressTimer.value) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    handleMovePoint(
      { x: touch.clientX ?? touch.pageX ?? 0, y: touch.clientY ?? touch.pageY ?? 0 },
      'touch'
    );
  };

  const handleTouchEnd = (event) => {
    if (event.touches?.length) return;
    finishInteraction('touch');
  };

  const handleTouchCancel = () => {
    finishInteraction('touch');
  };

  const handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if ('button' in event && event.button !== 0) return;
    startLongPress(
      { x: event.clientX ?? event.pageX ?? 0, y: event.clientY ?? event.pageY ?? 0 },
      'mouse'
    );
  };

  const handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    handleMovePoint(
      { x: event.clientX ?? event.pageX ?? 0, y: event.clientY ?? event.pageY ?? 0 },
      'mouse'
    );
  };

  const handlePointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    finishInteraction('mouse');
  };

  const handlePointerCancel = (event) => {
    if (event.pointerType === 'touch') return;
    finishInteraction('mouse');
  };

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    startLongPress({ x: event.clientX ?? 0, y: event.clientY ?? 0 }, 'mouse');
  };

  const handleMouseMove = (event) => {
    handleMovePoint({ x: event.clientX ?? 0, y: event.clientY ?? 0 }, 'mouse');
  };

  const handleMouseUp = () => {
    finishInteraction('mouse');
  };

  const handleMouseLeave = () => {
    finishInteraction('mouse');
  };

  wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
  wrapper.addEventListener('touchmove', handleTouchMove, { passive: true });
  wrapper.addEventListener('touchend', handleTouchEnd, { passive: true });
  wrapper.addEventListener('touchcancel', handleTouchCancel, { passive: true });

  if (HAS_POINTER_EVENTS) {
    wrapper.addEventListener('pointerdown', handlePointerDown, { passive: true });
    wrapper.addEventListener('pointermove', handlePointerMove, { passive: true });
    wrapper.addEventListener('pointerup', handlePointerUp, { passive: true });
    wrapper.addEventListener('pointercancel', handlePointerCancel, { passive: true });
    wrapper.addEventListener('pointerleave', handlePointerCancel, { passive: true });
  } else {
    wrapper.addEventListener('mousedown', handleMouseDown);
    wrapper.addEventListener('mousemove', handleMouseMove);
    wrapper.addEventListener('mouseup', handleMouseUp);
    wrapper.addEventListener('mouseleave', handleMouseLeave);
  }
};

const attachMediaControl = (element) => {
  const tagName = element?.tagName?.toLowerCase();
  if (!element || (tagName !== 'video' && tagName !== 'img')) return;
  if (boundElements.has(element)) return;
  boundElements.add(element);

  element.setAttribute('draggable', 'false');
  element.style.setProperty('-webkit-user-drag', 'none');
  element.addEventListener('dragstart', preventDragStart);

  if (window.storeeelItems?.isMobile) {
    element.style.setProperty('-webkit-touch-callout', 'none');
    element.style.setProperty('-webkit-user-select', 'none');
    element.style.userSelect = 'none';
    element.addEventListener('contextmenu', preventContextMenu);
  }

  const wrapper = element.closest?.('.slide-media-wrapper');
  if (wrapper) {
    attachWrapperHandlers(wrapper);
  }
};

const observeWrapperForMedia = (wrapper) => {
  if (!wrapper || observedWrappers.has(wrapper)) return;
  observedWrappers.add(wrapper);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches('video, img')) {
          attachMediaControl(node);
          return;
        }
        node.querySelectorAll?.('video, img').forEach((media) => {
          attachMediaControl(media);
        });
      });
    });
  });

  observer.observe(wrapper, { childList: true, subtree: true });
};

export const applyMediaLongPressHandlers = (element) => {
  if (!element) return;

  attachMediaControl(element);
  const wrapper = element.closest?.('.slide-media-wrapper');
  if (wrapper) {
    observeWrapperForMedia(wrapper);
  }
};
